import { useState } from 'react';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Convert FormData to URL-encoded format
    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d4mdf177s74mkk8pdv8g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-blue-500 rounded-full flex items-center justify-center">
                <i className="ri-swim-line text-2xl text-white"></i>
              </div>
              <span className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Poppins, sans-serif' }}>Laness Swimming</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">About</button>
              <button onClick={() => scrollToSection('programs')} className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">Programs</button>
              <button onClick={() => scrollToSection('schedule')} className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">Schedule</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">Contact</button>
              <button onClick={() => scrollToSection('contact')} className="bg-blue-600 text-white px-6 py-2.5 rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">Book Now</button>
            </div>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer">
              <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-700`}></i>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-gray-700 hover:text-blue-600 py-2 cursor-pointer">Home</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left text-gray-700 hover:text-blue-600 py-2 cursor-pointer">About</button>
              <button onClick={() => scrollToSection('programs')} className="block w-full text-left text-gray-700 hover:text-blue-600 py-2 cursor-pointer">Programs</button>
              <button onClick={() => scrollToSection('schedule')} className="block w-full text-left text-gray-700 hover:text-blue-600 py-2 cursor-pointer">Schedule</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-700 hover:text-blue-600 py-2 cursor-pointer">Contact</button>
              <button onClick={() => scrollToSection('contact')} className="bg-blue-600 text-white px-6 py-2.5 rounded-full hover:bg-blue-700 transition-colors w-full whitespace-nowrap cursor-pointer">Book Now</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-20 min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/e61cee738aec3c3ca1e51a89f5be6ac7/42cc7353cea64a454e34ee81ad1a3f35.jpeg"
            alt="Laness Swimming Lessons"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-blue-900/40 via-blue-900/30 to-blue-900/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <div className="text-center text-white">
            <h1 className="text-5xl md:text-7xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Professional Swimming Lessons
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              Expert instruction for beginners, adults, children, and teams in Adenta, New Legon
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => scrollToSection('contact')} className="bg-yellow-400 text-blue-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-yellow-300 transition-colors whitespace-nowrap cursor-pointer">
                Start Your Journey
              </button>
              <button onClick={() => scrollToSection('programs')} className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer border-2 border-white">
                View Programs
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Welcome to Laness Swimming
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We provide professional swimming instruction for all ages and skill levels. Our experienced coaches are dedicated to helping you achieve your swimming goals in a safe and supportive environment.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-medal-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Instructors</h3>
              <p className="text-gray-600">Certified and experienced swimming coaches dedicated to your success and safety in the water.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-group-line text-3xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">All Skill Levels</h3>
              <p className="text-gray-600">From complete beginners to advanced swimmers, we have programs tailored to your needs.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-map-pin-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Prime Location</h3>
              <p className="text-gray-600">Conveniently located in Adenta, New Legon with excellent facilities and easy access.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Our Programs
            </h2>
            <p className="text-lg text-gray-600">Professional swimming lessons tailored to your needs</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-user-star-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">Beginners</h3>
              <p className="text-gray-700 text-center mb-4">Perfect for those taking their first steps in the water. Learn basic swimming techniques and water safety.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-blue-600"></i>
                  <span>Water confidence building</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-blue-600"></i>
                  <span>Basic floating techniques</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-blue-600"></i>
                  <span>Introduction to strokes</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-8 hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-parent-line text-4xl text-yellow-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">Adults</h3>
              <p className="text-gray-700 text-center mb-4">Never too late to learn! Adult-focused lessons in a comfortable and supportive environment.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-yellow-600"></i>
                  <span>Flexible scheduling</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-yellow-600"></i>
                  <span>Fitness swimming</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-yellow-600"></i>
                  <span>Technique improvement</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-emotion-happy-line text-4xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">Children / Kids</h3>
              <p className="text-gray-700 text-center mb-4">Fun and engaging lessons designed specifically for children to develop swimming skills safely.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-blue-600"></i>
                  <span>Age-appropriate teaching</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-blue-600"></i>
                  <span>Water safety education</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-blue-600"></i>
                  <span>Fun learning activities</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-8 hover:shadow-xl transition-all hover:-translate-y-1">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 mx-auto">
                <i className="ri-team-line text-4xl text-yellow-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">Groups / Teams</h3>
              <p className="text-gray-700 text-center mb-4">Specialized training for swimming teams and group sessions with competitive focus.</p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-yellow-600"></i>
                  <span>Team building exercises</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-yellow-600"></i>
                  <span>Competitive training</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-yellow-600"></i>
                  <span>Group discounts available</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      <section id="schedule" className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Class Schedule
            </h2>
            <p className="text-lg text-gray-600">Find the perfect time for your swimming lessons</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-blue-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Day</th>
                    <th className="px-6 py-4 text-left font-semibold">Time</th>
                    <th className="px-6 py-4 text-left font-semibold">Program</th>
                    <th className="px-6 py-4 text-left font-semibold">Level</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-blue-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Monday</td>
                    <td className="px-6 py-4 text-gray-600">8:00 AM - 11:00 AM</td>
                    <td className="px-6 py-4 text-gray-600">All Programs</td>
                    <td className="px-6 py-4"><span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">All Levels</span></td>
                  </tr>
                  <tr className="hover:bg-blue-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Tuesday</td>
                    <td className="px-6 py-4 text-gray-600">8:00 AM - 11:00 AM</td>
                    <td className="px-6 py-4 text-gray-600">All Programs</td>
                    <td className="px-6 py-4"><span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">All Levels</span></td>
                  </tr>
                  <tr className="hover:bg-blue-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Wednesday</td>
                    <td className="px-6 py-4 text-gray-600">8:00 AM - 11:00 AM</td>
                    <td className="px-6 py-4 text-gray-600">All Programs</td>
                    <td className="px-6 py-4"><span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">All Levels</span></td>
                  </tr>
                  <tr className="hover:bg-blue-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Thursday</td>
                    <td className="px-6 py-4 text-gray-600">8:00 AM - 11:00 AM</td>
                    <td className="px-6 py-4 text-gray-600">All Programs</td>
                    <td className="px-6 py-4"><span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">All Levels</span></td>
                  </tr>
                  <tr className="hover:bg-blue-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Friday</td>
                    <td className="px-6 py-4 text-gray-600">8:00 AM - 11:00 AM</td>
                    <td className="px-6 py-4 text-gray-600">All Programs</td>
                    <td className="px-6 py-4"><span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">All Levels</span></td>
                  </tr>
                  <tr className="hover:bg-blue-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Saturday</td>
                    <td className="px-6 py-4 text-gray-600">10:00 AM - 7:00 PM</td>
                    <td className="px-6 py-4 text-gray-600">All Programs</td>
                    <td className="px-6 py-4"><span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">All Levels</span></td>
                  </tr>
                  <tr className="hover:bg-blue-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">Sunday</td>
                    <td className="px-6 py-4 text-gray-600">10:00 AM - 7:00 PM</td>
                    <td className="px-6 py-4 text-gray-600">All Programs</td>
                    <td className="px-6 py-4"><span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">All Levels</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
            <div className="flex items-start gap-3">
              <i className="ri-information-line text-2xl text-yellow-600 mt-1"></i>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Important Information</h4>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>• Private lessons available by appointment</li>
                  <li>• Group discounts for 5 or more participants</li>
                  <li>• Please arrive 15 minutes before your scheduled class</li>
                  <li>• All equipment provided - just bring your swimsuit and towel</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Get In Touch
            </h2>
            <p className="text-lg text-gray-600">Ready to start your swimming journey? Contact us today!</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-blue-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Phone Numbers</h3>
                    <a href="tel:0500008738" className="text-blue-600 hover:text-blue-700 block mb-1 cursor-pointer">0500008738</a>
                    <a href="tel:0548729792" className="text-blue-600 hover:text-blue-700 block cursor-pointer">0548729792</a>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-yellow-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                    <a href="mailto:trigerone054@gmail.com" className="text-blue-600 hover:text-blue-700 cursor-pointer">trigerone054@gmail.com</a>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-blue-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Location</h3>
                    <p className="text-gray-600">Adenta, New Legon<br />Accra, Ghana</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl text-yellow-600"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Operating Hours</h3>
                    <div className="space-y-1 text-gray-600 text-sm">
                      <p><strong>Monday - Friday:</strong> 8:00 AM - 11:00 AM</p>
                      <p><strong>Saturday - Sunday:</strong> 10:00 AM - 7:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Book Your Lesson</h3>
              <form className="space-y-6" data-readdy-form id="booking-form" onSubmit={handleSubmit}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input 
                    type="text" 
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input 
                    type="email" 
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <input 
                    type="tel" 
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="0500000000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Program *</label>
                  <select 
                    name="program"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select a program</option>
                    <option value="Beginners">Beginners</option>
                    <option value="Adults">Adults</option>
                    <option value="Children / Kids">Children / Kids</option>
                    <option value="Groups / Teams">Groups / Teams</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Day</label>
                  <select 
                    name="preferred_day"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Select a day</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                    <option value="Saturday">Saturday</option>
                    <option value="Sunday">Sunday</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea 
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-sm resize-none"
                    placeholder="Tell us about your swimming experience and goals (max 500 characters)"
                  ></textarea>
                </div>

                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    Thank you! Your booking request has been submitted successfully. We'll contact you soon.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    Something went wrong. Please try again or contact us directly.
                  </div>
                )}

                <button 
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Submitting...' : 'Submit Booking Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-500 to-teal-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                  <i className="ri-swim-line text-2xl text-teal-600"></i>
                </div>
                <span className="text-xl font-bold" style={{ fontFamily: 'Poppins, sans-serif' }}>Laness Swimming</span>
              </div>
              <p className="text-teal-50 text-sm">Professional swimming instruction for all ages and skill levels in Adenta, New Legon.</p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-teal-50">
                <li><button onClick={() => scrollToSection('home')} className="hover:text-white transition-colors cursor-pointer">Home</button></li>
                <li><button onClick={() => scrollToSection('about')} className="hover:text-white transition-colors cursor-pointer">About</button></li>
                <li><button onClick={() => scrollToSection('programs')} className="hover:text-white transition-colors cursor-pointer">Programs</button></li>
                <li><button onClick={() => scrollToSection('schedule')} className="hover:text-white transition-colors cursor-pointer">Schedule</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Programs</h4>
              <ul className="space-y-2 text-sm text-teal-50">
                <li>Beginners</li>
                <li>Adults</li>
                <li>Children / Kids</li>
                <li>Groups / Teams</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-teal-50">
                <li className="flex items-center gap-2">
                  <i className="ri-phone-line"></i>
                  <span>0500008738</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-phone-line"></i>
                  <span>0548729792</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line"></i>
                  <span>trigerone054@gmail.com</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-map-pin-line"></i>
                  <span>Adenta, New Legon</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-teal-400 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-teal-50">© 2025 Laness Swimming. All rights reserved.</p>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-teal-50 hover:text-white transition-colors cursor-pointer">
              Website Builder
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
